import React from 'react';
import styled from 'styled-components';
import type {
  BackgroundColorProps,
  BorderProps,
  SpaceProps,
  LayoutProps,
  TypographyProps,
} from 'styled-system';
import { border, color, space, layout, typography } from 'styled-system';
import Text from '../Text';

const Table = styled.table<{ layout?: 'auto' | 'fixed'; height?: string }>`
  width: 100%;
  border-collapse: collapse;
  table-layout: ${(props) => props.layout ?? 'auto'};
  ${(props) => props.height ?? `height: ${props.height}`};
`;

export interface TableHeadersProps
  extends BackgroundColorProps,
    SpaceProps,
    BorderProps {
  isSticky?: boolean;
}

export const TableHeader = styled.th<SpaceProps & BorderProps & LayoutProps>`
  background-color: ${(props) => props.theme.colors.neutral.white_100};
  color: ${(props) => props.theme.colors.neutral.gray_300};
  text-align: left;
  padding: ${(props) => props.theme.space[2]}px
    ${(props) => props.theme.space[3]}px;
  &:first-child {
    padding-left: ${(props) => props.theme.space[4]}px;
  }
  &:last-child {
    padding-right: ${(props) => props.theme.space[4]}px;
  }
  ${space}
  ${border}
  ${layout}
`;

export const TableHeaders = styled.tr<TableHeadersProps>`
  ${color}
  ${space}
  ${border}

  font-size: 0.875rem;
  borderbottom: 1px solid black;

  & > ${TableHeader} {
    position: ${(props) => (props.isSticky ? 'sticky' : 'static')};
    top: 0;
    z-index: 1;
  }
`;

export interface TableRowProps extends BorderProps {
  activeColor?: string;
  isActive?: boolean;
  isMuted?: boolean;
  height?: string | number;
  position?: string;
}

export const TableRow = styled.tr<TableRowProps>`
  background-color: ${(props) =>
    props.isActive
      ? props.activeColor || '#ffe552'
      : props.isMuted
        ? '#fff'
        : '#fff'};
  ${({ height }) =>
    height ? `height: ${height}${typeof height === 'number' ? 'px' : ''};` : ''}
  border: 1px solid #00000012
`;

export type TableCellProps = SpaceProps &
  TypographyProps &
  LayoutProps & {
    cursor?: 'pointer' | 'default';
  };

export const TableCell = styled.td<TableCellProps>`
  font-family: 'Inter', 'Space Grotesk', 'Helvetica', fallback-font, sans-serif;
  padding: ${(props) => props.theme.space[2]}px
    ${(props) => props.theme.space[3]}px;
  &:first-child {
    padding-left: ${(props) => props.theme.space[4]}px;
  }
  &:last-child {
    padding-right: ${(props) => props.theme.space[4]}px;
  }
  line-height: 1em;
  font-weight: 400;
  text-align: left;
  max-width: 25vw;
  font-size: 0.875rem;
  height: 37px;
  color: #000;
  ${space}
  ${typography}
  ${layout}
  cursor: ${(props) => props.cursor ?? 'default'};
`;

export default Table;

export const NotFoundTableRow = ({
  colSpan,
  isLoading,
  isSearching,
}: {
  colSpan: number;
  isSearching: boolean;
  isLoading?: boolean;
}) => {
  const noDataText = isSearching
    ? 'No values found matching the search criteria'
    : 'No records found';

  const loadingText = 'Loading ...';
  return (
    <TableRow height="100%">
      <TableCell colSpan={colSpan} textAlign="center">
        <Text fontSize="1.2em">{isLoading ? loadingText : noDataText}</Text>
      </TableCell>
    </TableRow>
  );
};
